import { ActionStatus } from '../../../common/constants/action-status';
import { WishListState } from '../interfaces/wish-list-states.interface';
import { addProductToWishList } from '../thunks/wish-list-add-product.thunk';
import { addWishList } from '../thunks/wish-list-create.thunk';
import { createSlice } from '@reduxjs/toolkit';
import { defaultApiState } from '../../../common/constants/api-constants';
import { deleteWishList } from '../thunks/wish-list-delete.thunk';
import { editWishList } from '../thunks/wish-list-edit.thunk';
import { fetchAllWishLists } from '../thunks/wish-list-fetch-all-wish-lists.thunks';
import { fetchWishListBySharingCode } from '../thunks/wish-list fetch-by-sharing-code.thunk';
import { fetchWishListByWishListId } from '../thunks/wish-list-fetch-wish-list-by-id.thunks';
import { removeProductFromWishList } from '../thunks/wish-list-remove-product.thunk';

/*----- CREATE INITIAL STATE ------*/

export const wishListInitialState: WishListState = {
  addProductToWishList: defaultApiState,
  removeProductFromWishList: defaultApiState,
  myAccountWishLists: defaultApiState,
  createWishList: defaultApiState,
  deleteWishList: defaultApiState,
  editWishList: defaultApiState,
  getWishListById: defaultApiState,
  getWishListBySharingCode: defaultApiState,
  currentWishList: undefined,
  updateProductOnWishListComment: '',
};

/*----- CREATE STORE SLICE ------*/

export const wishListSlice = createSlice({
  name: 'wishList',
  initialState: wishListInitialState,
  reducers: {
    clearUpdateProductOnWishListComment(state) {
      state.updateProductOnWishListComment = '';
    },
    setUpdateProductOnWishListComment(state, action) {
      state.updateProductOnWishListComment = action.payload;
    },
    resetCreateWishList(state) {
      state.createWishList = defaultApiState;
    },
    resetEditWishList(state) {
      state.editWishList = defaultApiState;
    },
    setCurrentWishList(state, action) {
      state.currentWishList = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAllWishLists.pending, (state) => {
        state.myAccountWishLists.status = ActionStatus.loading;
        state.myAccountWishLists.data = undefined;
      })
      .addCase(fetchAllWishLists.fulfilled, (state, action) => {
        state.myAccountWishLists.status = ActionStatus.succeeded;
        state.myAccountWishLists.data = action.payload;
      })
      .addCase(fetchAllWishLists.rejected, (state, action) => {
        state.myAccountWishLists.status = ActionStatus.failed;
        state.myAccountWishLists.error = action.error.message || '';
      })
      .addCase(addWishList.pending, (state) => {
        state.createWishList.status = ActionStatus.loading;
        state.createWishList.data = undefined;
      })
      .addCase(addWishList.fulfilled, (state, action) => {
        state.createWishList.status = ActionStatus.succeeded;
        state.createWishList.data = action.payload;
      })
      .addCase(addWishList.rejected, (state, action) => {
        state.createWishList.status = ActionStatus.failed;
        state.createWishList.error = action.error.message || '';
        state.createWishList.data = undefined;
      })
      .addCase(deleteWishList.pending, (state) => {
        state.deleteWishList.status = ActionStatus.loading;
        state.deleteWishList.data = undefined;
      })
      .addCase(deleteWishList.fulfilled, (state, action) => {
        state.deleteWishList.status = ActionStatus.succeeded;
        state.deleteWishList.data = action.payload;
      })
      .addCase(deleteWishList.rejected, (state, action) => {
        state.deleteWishList.status = ActionStatus.failed;
        state.deleteWishList.error = action.error.message || '';
        state.deleteWishList.data = undefined;
      })
      .addCase(editWishList.pending, (state) => {
        state.editWishList.status = ActionStatus.loading;
        state.editWishList.data = undefined;
      })
      .addCase(editWishList.fulfilled, (state, action) => {
        state.editWishList.status = ActionStatus.succeeded;
        state.editWishList.data = action.payload;
      })
      .addCase(editWishList.rejected, (state, action) => {
        state.editWishList.status = ActionStatus.failed;
        state.editWishList.error = action.error.message || '';
        state.editWishList.data = undefined;
      })
      .addCase(fetchWishListByWishListId.pending, (state) => {
        state.getWishListById.status = ActionStatus.loading;
        state.getWishListById.data = undefined;
      })
      .addCase(fetchWishListByWishListId.fulfilled, (state, action) => {
        state.getWishListById.status = ActionStatus.succeeded;
        state.getWishListById.data = action.payload;
      })
      .addCase(fetchWishListByWishListId.rejected, (state, action) => {
        state.getWishListById.status = ActionStatus.failed;
        state.getWishListById.error = action.error.message || '';
      })
      .addCase(addProductToWishList.pending, (state) => {
        state.addProductToWishList.status = ActionStatus.loading;
        state.addProductToWishList.data = undefined;
      })
      .addCase(addProductToWishList.fulfilled, (state, action) => {
        state.addProductToWishList.status = ActionStatus.succeeded;
        state.addProductToWishList.data = action.payload;
      })
      .addCase(addProductToWishList.rejected, (state, action) => {
        state.addProductToWishList.status = ActionStatus.failed;
        state.addProductToWishList.error = action.error.message || '';
        state.addProductToWishList.data = undefined;
      })
      .addCase(removeProductFromWishList.pending, (state) => {
        state.removeProductFromWishList.status = ActionStatus.loading;
        state.removeProductFromWishList.data = undefined;
      })
      .addCase(removeProductFromWishList.fulfilled, (state, action) => {
        state.removeProductFromWishList.status = ActionStatus.succeeded;
        state.removeProductFromWishList.data = action.payload;
      })
      .addCase(removeProductFromWishList.rejected, (state, action) => {
        state.removeProductFromWishList.status = ActionStatus.failed;
        state.removeProductFromWishList.error = action.error.message || '';
        state.removeProductFromWishList.data = undefined;
      })
      .addCase(fetchWishListBySharingCode.pending, (state) => {
        state.getWishListBySharingCode.status = ActionStatus.loading;
        state.getWishListBySharingCode.data = undefined;
      })
      .addCase(fetchWishListBySharingCode.fulfilled, (state, action) => {
        state.getWishListBySharingCode.status = ActionStatus.succeeded;
        state.getWishListBySharingCode.data = action.payload;
      })
      .addCase(fetchWishListBySharingCode.rejected, (state, action) => {
        state.getWishListBySharingCode.status = ActionStatus.failed;
        state.getWishListBySharingCode.error = action.error.message || '';
      });
  },
});

/*----- CREATE SLICE ACTIONS ------*/

export const {
  clearUpdateProductOnWishListComment,
  setUpdateProductOnWishListComment,
  resetCreateWishList,
  resetEditWishList,
  setCurrentWishList,
} = wishListSlice.actions;

/*----- CREATE REDUCER ------*/

export const wishListReducer = wishListSlice.reducer;